import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { useCallback } from "react";
import {
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaPaperclip,
} from "react-icons/fa";
import Flex from "components/common/Flex";
import FalconLightBox from "components/common/FalconLightBox";
import { Image } from "react-bootstrap";

/* ---------- helpers ---------- */

const getExtension = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.split(".").pop().toLowerCase();
  }
  return value.name?.split(".").pop().toLowerCase();
};

const getFileType = (file, preview) => {
  const ext = getExtension(file || preview);

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["ppt", "pptx"].includes(ext)) return "ppt";

  return "other";
};

/* ---------- component ---------- */

const DocumentDropzone = ({ index }) => {
  const { setValue, watch } = useFormContext();

  const preview = watch(`storeDocuments.${index}.preview`);
  const file = watch(`storeDocuments.${index}.file`);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const f = acceptedFiles[0];
      if (!f) return;

      const previewUrl = URL.createObjectURL(f);

      setValue(`storeDocuments.${index}.file`, f, { shouldDirty: true });
      setValue(`storeDocuments.${index}.preview`, previewUrl);
    },
    [index, setValue],
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "application/vnd.ms-powerpoint": [],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [],
    },
    onDrop,
  });

  const openInNewTab = (e) => {
    e.stopPropagation();
    if (preview) {
      window.open(preview, "_blank", "noopener,noreferrer");
    }
  };

  const fileType = getFileType(file, preview);

  const renderPreview = () => {
    if (!preview) return null;

    switch (fileType) {
      case "image":
        return (
          <FalconLightBox image={preview}>
            <Image
              src={preview}
              rounded
              width={80}
              height={40}
              style={{ cursor: "pointer", objectFit: "cover" }}
            />
          </FalconLightBox>
        );

      case "pdf":
        return (
          <FaFilePdf
            size={30}
            color="#e63946"
            style={{ cursor: "pointer" }}
            title="Open PDF"
            onClick={() => openInNewTab(preview)}
          />
        );

      case "word":
        return (
          <FaFileWord
            size={30}
            color="#2b579a"
            style={{ cursor: "pointer" }}
            title="Open Word file"
            onClick={() => openInNewTab(preview)}
          />
        );

      case "excel":
        return (
          <FaFileExcel
            size={30}
            color="#217346"
            style={{ cursor: "pointer" }}
            title="Open Excel file"
            onClick={() => openInNewTab(preview)}
          />
        );

      case "ppt":
        return (
          <FaFilePowerpoint
            size={30}
            color="#d24726"
            style={{ cursor: "pointer" }}
            title="Open PowerPoint"
            onClick={() => openInNewTab(preview)}
          />
        );

      default:
        return (
          <FaPaperclip
            size={26}
            style={{ cursor: "pointer" }}
            title="Open file"
            onClick={() => openInNewTab(preview)}
          />
        );
    }
  };

  return (
    <div className="d-flex align-items-center gap-4">
      {/* Upload icon (dropzone trigger) */}
      <div
        {...getRootProps()}
        style={{ cursor: "pointer", display: "inline-block" }}
      >
        <input {...getInputProps({ multiple: false })} />
        <FaImage size={27} title="Upload file" />
      </div>

      {/* Preview / file icon (separate) */}
      {preview && (
        <Flex alignItems="center" gap={2}>
          {renderPreview()}
        </Flex>
      )}
    </div>
  );
};

export default DocumentDropzone;
